import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ArrowUpRight, Download, FileText } from "lucide-react";
import { useDepartments } from "@/context/DepartmentsContext";
import { useSite, useUrl } from "@/context/SiteContext";
import { SmartLink } from "@/components/SmartLink";
import {
  MaskedLines,
  Reveal,
  Lightbox,
  Gallery,
  ParallaxImage,
} from "@/components/motion";

/**
 * SEO helper — sets document.title + meta description on mount.
 */
function useDepartmentMeta(dept) {
  useEffect(() => {
    if (!dept) return;
    const title = dept.meta_title || `${dept.name} — SUNTEC Group`;
    const desc = dept.meta_description || dept.summary || "";
    document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", desc.slice(0, 160));
    return () => {
      document.title = "SUNTEC Group";
    };
  }, [dept]);
}

/* -------------------------------------------------------------------------- */
/*                                LIST PAGE                                   */
/* -------------------------------------------------------------------------- */

export function DepartmentsListPage() {
  const { departments, loaded } = useDepartments();
  const url = useUrl();
  const { t } = useSite();
  const items = departments || [];

  return (
    <div data-testid="departments-list-page">
      <section className="border-b border-black">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-16 md:py-24">
          <div className="font-mono-label text-black/60">{t("section.departments.hero.eyebrow")}</div>
          <h1 className="mt-4 font-display font-medium uppercase text-5xl md:text-8xl tracking-tighter leading-[0.9]">
            <MaskedLines lines={[`${items.length || "Nouă"} ${t("section.departments.hero.title_1")}`, t("section.departments.hero.title_2")]} />
          </h1>
        </div>
      </section>
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-14">
        {!loaded ? (
          <div className="font-mono-label text-black/60">Se încarcă...</div>
        ) : items.length === 0 ? (
          <div className="border border-black p-12 text-center font-mono-label text-black/60">
            Nu există departamente.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black border border-black">
            {items.map((d, i) => (
              <motion.div
                key={d.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={url.dept(d.slug)}
                  data-testid={`list-dept-${d.slug}`}
                  className="group bg-white hover:bg-black hover:text-white transition-all p-8 min-h-[240px] flex flex-col justify-between h-full"
                >
                  <div className="flex items-start justify-between">
                    <div className="font-mono-label opacity-60">{d.number}</div>
                    <ArrowUpRight
                      size={22}
                      className="opacity-40 group-hover:opacity-100 group-hover:rotate-45 transition-all duration-500"
                    />
                  </div>
                  <div>
                    <div className="font-display font-medium uppercase text-3xl md:text-4xl tracking-tighter">
                      {d.name}
                    </div>
                    <div className="mt-3 text-base opacity-70 leading-relaxed max-w-md">{d.summary}</div>
                  </div>
                </Link>
              </motion.div>
            ))}
            {items.length % 2 !== 0 && (
              <div aria-hidden className="hidden md:block bg-white" data-testid="list-grid-filler" />
            )}
          </div>
        )}
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                             DETAIL PAGE (PREMIUM)                          */
/* -------------------------------------------------------------------------- */

export function DepartmentDetailPage({ slug: propSlug }) {
  const { id } = useParams();
  const nav = useNavigate();
  const { departments, allDepartments, findBySlug, loaded } = useDepartments();
  const url = useUrl();
  const { t } = useSite();

  const activeSlug = propSlug || id;
  // Hidden landing pages (like /achizitii) live in allDepartments but not in
  // the public list. Use findBySlug to resolve both.
  const dept = useMemo(
    () => (findBySlug ? findBySlug(activeSlug) : departments.find((d) => d.slug === activeSlug)),
    [findBySlug, departments, activeSlug]
  );
  // Prev/next only cycles through the visible (nav-listed) departments.
  const inNav = useMemo(
    () => departments.findIndex((d) => d.slug === activeSlug),
    [departments, activeSlug]
  );
  const next = inNav >= 0 && departments.length ? departments[(inNav + 1) % departments.length] : null;
  const prev = inNav >= 0 && departments.length ? departments[(inNav - 1 + departments.length) % departments.length] : null;

  useDepartmentMeta(dept);

  // Scroll progress indicator
  const { scrollYProgress } = useScroll();
  const progressWidth = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  // Lightbox state
  const [lbIndex, setLbIndex] = useState(null);
  const gallery = dept?.gallery || [];
  const openLb = (i) => setLbIndex(i);
  const closeLb = () => setLbIndex(null);
  const nextLb = () => setLbIndex((i) => (i + 1) % gallery.length);
  const prevLb = () => setLbIndex((i) => (i - 1 + gallery.length) % gallery.length);

  if (!loaded) {
    return (
      <div className="mx-auto max-w-[1440px] px-6 py-24 font-mono-label text-black/60" data-testid="dept-loading">
        Se încarcă departamentul...
      </div>
    );
  }

  if (!dept) {
    return (
      <div className="mx-auto max-w-[1440px] px-6 py-24 text-center" data-testid="dept-not-found">
        <div className="font-display font-medium uppercase text-4xl">Departament negăsit</div>
        <button className="mt-6 font-mono-label underline" onClick={() => nav(url.departments)}>
          Înapoi
        </button>
      </div>
    );
  }

  // Compose hero title lines from name — adapt font size and line-split to length
  const nameParts = dept.name.split(/\s+/);
  const isLongName = dept.name.length >= 22;
  const heroLines =
    !isLongName && nameParts.length > 1
      ? [nameParts.slice(0, Math.ceil(nameParts.length / 2)).join(" "), nameParts.slice(Math.ceil(nameParts.length / 2)).join(" ")]
      : [dept.name];
  const heroSizeClass = isLongName
    ? "text-5xl sm:text-7xl lg:text-[6.5rem]"
    : "text-6xl sm:text-8xl lg:text-[9rem]";

  return (
    <div data-testid={`dept-detail-${dept.slug}`}>
      {/* Scroll progress bar */}
      <motion.div
        style={{ scaleX: progressWidth }}
        className="fixed top-0 left-0 right-0 h-[2px] bg-black origin-left z-50"
        aria-hidden
      />

      {/* Breadcrumb */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 pt-10">
        {activeSlug === "achizitii-proiecte-europene" ? (
          <Link to="/" data-testid="back-to-home" className="font-mono-label inline-flex items-center gap-2 hover:underline">
            <ArrowLeft size={14} /> Acasă
          </Link>
        ) : (
          <Link to={url.departments} data-testid="back-to-list" className="font-mono-label inline-flex items-center gap-2 hover:underline">
            <ArrowLeft size={14} /> Toate departamentele
          </Link>
        )}
      </div>

      {/* ================ 01 · HERO (kinetic masked reveal) ================ */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-12 border-b border-black">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-mono-label text-black/60"
            >
              {dept.number} / {activeSlug === "achizitii-proiecte-europene" ? "Serviciu" : "Departament"}
            </motion.div>
          </div>
          <div className="lg:col-span-10">
            <h1 className={`font-display font-medium uppercase tracking-tighter leading-[0.9] ${heroSizeClass}`}>
              {isLongName ? (
                <motion.span
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  className="block"
                >
                  {dept.name}
                </motion.span>
              ) : (
                <MaskedLines lines={heroLines} delay={0.15} />
              )}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5 + heroLines.length * 0.11 }}
              className="mt-10 text-base md:text-lg max-w-3xl text-black/80 leading-snug"
            >
              {dept.tagline}
            </motion.p>
          </div>
        </div>
      </section>

      {/* ================ Parallax hero image ================ */}
      {dept.cover_image && (
        <section className="relative border-b border-black">
          <div className="relative h-[55vh] md:h-[75vh] overflow-hidden">
            <ParallaxImage src={dept.cover_image} alt={dept.name} />
            <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 font-mono-label text-white mix-blend-difference">
              [ {dept.number} / SUNTEC GROUP ]
            </div>
          </div>
        </section>
      )}

      {/* ================ 02 · Manifesto — Ce facem ================ */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-4 md:sticky md:top-24 self-start">
          <div className="font-mono-label text-black/60">{t("section.dept.manifest.eyebrow")}</div>
          <h2 className="mt-4 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9]">
            {t("section.dept.manifest.title_1")}<br />
            <span className="italic font-normal">{t("section.dept.manifest.title_2")}</span>
          </h2>
        </Reveal>
        <div className="md:col-span-8">
          <Reveal>
            <p className="text-base leading-relaxed text-black/85 max-w-2xl">{dept.summary}</p>
            {dept.long_text && (
              <p className="mt-6 text-base leading-relaxed text-black/70 max-w-2xl">{dept.long_text}</p>
            )}
          </Reveal>

          {/* Services list — numbered */}
          {dept.services && dept.services.length > 0 && (
            <Reveal delay={0.1}>
              <div className="mt-16">
                <div className="font-mono-label text-black/60 mb-4">{t("section.dept.services.eyebrow")}</div>
                <ul className="border-t border-black">
                  {dept.services.map((s, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.4 }}
                      transition={{ duration: 0.5, delay: i * 0.04 }}
                      data-testid={`service-${i}`}
                      className="border-b border-black py-5 flex items-start gap-4 group hover:bg-black hover:text-white transition-colors duration-300 -mx-4 px-4"
                    >
                      <span className="font-mono-label opacity-50 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <Check size={16} className="mt-1 shrink-0 opacity-70" />
                      <span className="text-base">{s}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* ================ Consultanță SUNTEC (achiziții-proiecte-europene only) ================ */}
      {activeSlug === "achizitii-proiecte-europene" && (
        <section
          className="relative border-y border-black bg-white overflow-hidden"
          data-testid="consultanta-section"
        >
          {/* Big monogram in background */}
          <div
            aria-hidden
            className="absolute -right-8 -bottom-16 md:-right-16 md:-bottom-24 select-none pointer-events-none font-display font-medium uppercase text-black/[0.04] leading-none tracking-tighter text-[220px] md:text-[420px]"
          >
            01A
          </div>

          <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              {/* Left column — eyebrow + numbering */}
              <Reveal className="lg:col-span-3">
                <div className="font-mono-label text-black/60 flex items-center gap-3">
                  <span className="inline-block w-8 h-px bg-black" />
                  <span>02A / Consultanță</span>
                </div>
                <div className="mt-6 font-mono-label text-black/40 text-[11px]">
                  Proces end-to-end<br />
                  · Documente<br />
                  · Tehnicieni<br />
                  · Post-implementare
                </div>
              </Reveal>

              {/* Right column — title + subtitle + body + CTA */}
              <div className="lg:col-span-9">
                <Reveal>
                  <h2 className="font-display font-medium uppercase text-5xl md:text-7xl tracking-tighter leading-[0.9]">
                    Consultanță <span className="italic font-normal">SUNTEC</span>
                  </h2>
                </Reveal>

                <Reveal delay={0.1}>
                  <div className="mt-6 max-w-3xl text-lg md:text-xl text-black leading-snug font-medium">
                    Cum se desfășoară serviciul de consultanță pentru achiziții
                    în proiecte de fonduri europene.
                  </div>
                </Reveal>

                <Reveal delay={0.2}>
                  <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 border-t border-black pt-10">
                    <div className="md:col-span-1 hidden md:block font-mono-label text-black/40 text-[10px]">
                      —
                    </div>
                    <p
                      className="md:col-span-11 text-base md:text-[17px] text-black/80 leading-relaxed max-w-3xl"
                      data-testid="consultanta-body"
                    >
                      Procesul nostru este conceput pentru a-ți asigura succesul
                      de la prima consultanție și până la implementarea finală.
                      Colaborăm strâns cu parteneri de încredere și îți oferim
                      soluții personalizate pentru a transforma viziunea ta în
                      realitate, simplificând fiecare etapă a procesului de
                      finanțare și implementare a proiectului. Datorită
                      experienței noastre solide, acest flux funcționează
                      perfect. Suntem specializați în pregătirea tuturor
                      documentelor necesare, inclusiv procese verbale,
                      certificate de garanție, declarații de conformitate,
                      procese verbale de predare/primire, procese verbale de
                      punere în funcțiune, rapoarte justificative și procese
                      verbale de recepție. Sistemul nostru este meticulos
                      gândit, iar tu nu mai trebuie să ai grija documentelor
                      bine elaborate. Avem tehnicieni specializați în toate
                      domeniile de activitate și te asigurăm de un suport și
                      sprijin post-proiect deplin.
                    </p>
                  </div>
                </Reveal>

                <Reveal delay={0.3}>
                  <div className="mt-12 flex items-center gap-6 flex-wrap">
                    <Link
                      to="/contact"
                      data-testid="consultanta-cta"
                      className="group font-mono-label bg-black text-white px-8 py-5 hover:bg-white hover:text-black border border-black inline-flex items-center gap-3 transition-all"
                    >
                      Solicită Consultanță
                      <ArrowRight
                        size={16}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </Link>
                    <div className="font-mono-label text-black/50 text-[11px]">
                      Răspundem în ≤ 24h · L–V · 09:00–18:00
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ================ Digital services grid (digital dept only) ================ */}
      {activeSlug === "digital" && (
        <section
          className="border-b border-black"
          data-testid="digital-services-section"
        >
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
            <Reveal>
              <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                <div>
                  <div className="font-mono-label text-black/60">Servicii dedicate</div>
                  <h2 className="mt-3 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9]">
                    Patru direcții,<br />
                    <span className="italic font-normal">un ecosistem</span> digital.
                  </h2>
                </div>
                <div className="font-mono-label text-black/50">[ 04 / 04 ]</div>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black border border-black">
              {[
                {
                  n: "01",
                  t: "Fezabilitate digitală",
                  d: "Studiu de maturitate tehnologică și dosar aliniat ghidurilor de finanțare europene.",
                  to: "/fezabilitate-digitala",
                },
                {
                  n: "02",
                  t: "Digitalizare",
                  d: "Soluții complete IT&C și marketing — hardware, software, licențe, consultanță, instruire.",
                  to: "/digitalizare",
                },
                {
                  n: "03",
                  t: "Marketing",
                  d: "Strategie și execuție integrată — brand, conținut, publicitate online și offline.",
                  to: "/marketing",
                },
                {
                  n: "04",
                  t: "Dezvoltare website",
                  d: "Platforme WordPress și webshop-uri — cu SEO, GDPR, hosting și mentenanță lunară.",
                  to: "/dezvoltare-website",
                },
              ].map((s, i) => (
                <motion.div
                  key={s.to}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={s.to}
                    data-testid={`digital-service-${i}`}
                    className="group bg-white hover:bg-black hover:text-white transition-all p-8 md:p-10 min-h-[260px] flex flex-col justify-between h-full"
                  >
                    <div className="flex items-start justify-between">
                      <div className="font-mono-label opacity-60">{s.n}</div>
                      <ArrowUpRight
                        size={22}
                        className="opacity-40 group-hover:opacity-100 group-hover:rotate-45 transition-all duration-500"
                      />
                    </div>
                    <div>
                      <div className="font-display font-medium uppercase text-3xl md:text-4xl tracking-tighter leading-[0.95]">
                        {s.t}
                      </div>
                      <div className="mt-3 text-base opacity-70 leading-relaxed max-w-md">
                        {s.d}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================ Audituri services grid (audituri dept only) ================ */}
      {activeSlug === "audituri" && (
        <section
          className="border-b border-black"
          data-testid="audituri-services-section"
        >
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
            <Reveal>
              <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                <div>
                  <div className="font-mono-label text-black/60">Servicii de audit</div>
                  <h2 className="mt-3 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9]">
                    Patru instrumente,<br />
                    <span className="italic font-normal">un dosar</span> conform.
                  </h2>
                </div>
                <div className="font-mono-label text-black/50">[ 04 / 04 ]</div>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black border border-black">
              {[
                {
                  n: "01",
                  t: "Studiu identificare soluție IT",
                  d: "Analiza pe teren care traduce nevoile companiei în specificațiile tehnice ale proiectului.",
                  to: "/studiu-privind-identificarea-solutiei-it-digitalizare",
                },
                {
                  n: "02",
                  t: "Proiect informatic",
                  d: "Digitalizare completă — de la arhivă electronică la ecosistem IT&C integrat.",
                  to: "/proiect-informatic-pentru-digitalizare",
                },
                {
                  n: "03",
                  t: "Audit DESI",
                  d: "Evaluare tehnică folosind cei 12 indicatori Digital Economy and Society Index.",
                  to: "/audit-desi-pentru-digitalizare",
                },
                {
                  n: "04",
                  t: "Audit maturitate digitală",
                  d: "Digital Maturity Assessment — pre-implementare și final, semnate de specialiști diferiți.",
                  to: "/audit-de-maturitate-digitala",
                },
              ].map((s, i) => (
                <motion.div
                  key={s.to}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={s.to}
                    data-testid={`audituri-service-${i}`}
                    className="group bg-white hover:bg-black hover:text-white transition-all p-8 md:p-10 min-h-[260px] flex flex-col justify-between h-full"
                  >
                    <div className="flex items-start justify-between">
                      <div className="font-mono-label opacity-60">{s.n}</div>
                      <ArrowUpRight
                        size={22}
                        className="opacity-40 group-hover:opacity-100 group-hover:rotate-45 transition-all duration-500"
                      />
                    </div>
                    <div>
                      <div className="font-display font-medium uppercase text-3xl md:text-4xl tracking-tighter leading-[0.95]">
                        {s.t}
                      </div>
                      <div className="mt-3 text-base opacity-70 leading-relaxed max-w-md">
                        {s.d}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================ 03 · Highlights (kW capacities) ================ */}
      {dept.highlights && dept.highlights.length > 0 && (
        <section className="border-y border-black bg-black text-white">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-6">
            <div className="font-mono-label text-white/50 py-6">{t("section.dept.highlights.eyebrow")}</div>
          </div>
          <div className="mx-auto max-w-[1440px] grid grid-cols-1 md:grid-cols-3 gap-px bg-white/20 border-t border-white/20">
            {dept.highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="bg-black p-8 md:p-14"
              >
                <div className="font-display font-medium text-6xl md:text-8xl tracking-tighter leading-none">
                  {h.k}
                </div>
                <div className="mt-4 font-mono-label text-white/60">{h.v}</div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ================ 04 · Portofoliu / Galerie ================ */}
      {gallery.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-b border-black">
          <Reveal className="mb-10">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="font-mono-label text-black/60">{t("section.dept.gallery.eyebrow")}</div>
                <h2 className="mt-3 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9]">
                  {t("section.dept.gallery.title_1")}<br />
                  <span className="italic font-normal">{t("section.dept.gallery.title_2")}</span>
                </h2>
              </div>
              <div className="font-mono-label text-black/50">
                [ {String(gallery.length).padStart(2, "0")} imagini ]
              </div>
            </div>
          </Reveal>
          <Gallery images={gallery} onOpen={openLb} />
        </section>
      )}

      {/* ================ 05 · EU Programs ================ */}
      {dept.programs && dept.programs.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-b border-black">
          <Reveal className="mb-10">
            <div className="font-mono-label text-black/60">{t("section.dept.programs.eyebrow")}</div>
            <h2 className="mt-3 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9]">
              {t("section.dept.programs.title_1")}<br />
              <span className="italic font-normal">{t("section.dept.programs.title_2")}</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black border border-black">
            {dept.programs.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: (i % 2) * 0.08 }}
                className="bg-white p-8 md:p-10 flex flex-col"
              >
                <div className="font-display font-medium uppercase text-2xl">{p.name}</div>
                {p.amount && <div className="font-mono-label text-black/60 mt-2">{p.amount}</div>}
                {p.desc && <p className="mt-4 text-base text-black/75 leading-relaxed">{p.desc}</p>}
                {p.link && (
                  <a
                    href={p.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`program-link-${i}`}
                    className="mt-6 font-mono-label inline-flex items-center gap-2 border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors w-fit"
                  >
                    Vezi programul <ArrowRight size={12} />
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ================ 06 · Referințe / Procesul ================ */}
      {dept.references && dept.references.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-b border-black">
          <Reveal className="mb-10">
            <div className="font-mono-label text-black/60">
              {activeSlug === "achizitii-proiecte-europene" ? "Consultanță SUNTEC" : t("section.dept.references.eyebrow").replace(/^\s*/, "")}
            </div>
            <h2 className="mt-3 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9]">
              {activeSlug === "achizitii-proiecte-europene" ? (
                <>Procesul<span className="italic font-normal">.</span></>
              ) : (
                t("section.dept.references.title")
              )}
            </h2>
            {activeSlug === "achizitii-proiecte-europene" && (
              <p className="mt-6 max-w-2xl text-base text-black/70 leading-relaxed" data-testid="process-intro">
                Procesul nostru transformă achiziția din complexitate în claritate.
                Colaborăm strâns, îți oferim soluții personalizate și pregătim
                documentele cu precizie — de la prima consultație până la suport
                post-implementare.
              </p>
            )}
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black border border-black">
            {dept.references.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: (i % 2) * 0.08 }}
                className="bg-white p-8 md:p-10"
              >
                <div className="font-display font-medium uppercase text-2xl">{r.title}</div>
                <p className="mt-4 text-base text-black/75 leading-relaxed">{r.desc}</p>
              </motion.div>
            ))}
            {dept.references.length % 2 !== 0 && (
              <div aria-hidden className="hidden md:block bg-white" />
            )}
          </div>
        </section>
      )}

      {/* ================ Domenii ================ */}
      {dept.domains && dept.domains.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-16 md:py-24 border-b border-black">
          <Reveal className="mb-8">
            <div className="font-mono-label text-black/60">{t("section.dept.domains.eyebrow")}</div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-2">
              {dept.domains.map((dom, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.03 }}
                  className="inline-block border border-black px-4 py-2 text-sm hover:bg-black hover:text-white transition-colors cursor-default"
                >
                  {dom}
                </motion.span>
              ))}
            </div>
          </Reveal>
        </section>
      )}

      {/* ================ Values ================ */}
      {dept.values && dept.values.length > 0 && (
        <section className="border-b border-black bg-black text-white">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-6">
            <div className="font-mono-label text-white/50 py-6">{t("section.dept.values.eyebrow")}</div>
          </div>
          <div className="mx-auto max-w-[1440px] grid grid-cols-1 md:grid-cols-3 gap-px bg-white/20 border-t border-white/20">
            {dept.values.map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="bg-black p-8 md:p-12"
              >
                <div className="font-display font-medium uppercase text-2xl">{v.t}</div>
                <p className="mt-3 text-base text-white/70 leading-relaxed">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ================ Brochure download (Solar only) ================ */}
      {activeSlug === "solutii-fotovoltaice" && (
        <section
          className="relative border-b border-black bg-black text-white overflow-hidden"
          data-testid="brochure-download-section"
        >
          {/* Diagonal decorative grid lines */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, transparent 0 22px, #fff 22px 23px)",
            }}
          />
          <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <Reveal className="lg:col-span-7">
              <div className="font-mono-label text-white/50 flex items-center gap-3">
                <span className="inline-block w-8 h-px bg-white/50" />
                <FileText size={12} /> Broșură · PDF
              </div>
              <h2 className="mt-5 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.92]">
                Descarcă broșura<br />
                <span className="italic font-normal">SUNTEC Solar</span>
              </h2>
              <p className="mt-6 max-w-xl text-base text-white/70 leading-relaxed">
                Toate detaliile tehnice, tipurile de sisteme fotovoltaice și
                proiectele realizate — într-un document complet, pregătit pentru
                echipa ta de decizie.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5 lg:justify-self-end w-full lg:w-auto">
              <a
                href="/brosura-suntec-solar.pdf"
                download
                data-testid="brochure-download-btn"
                className="group font-mono-label bg-white text-black px-8 py-6 hover:bg-black hover:text-white border border-white inline-flex items-center justify-between gap-6 transition-all w-full lg:w-auto"
              >
                <span className="flex flex-col items-start gap-1">
                  <span className="text-[10px] opacity-60">Format PDF · ~40 MB</span>
                  <span>Descarcă broșura</span>
                </span>
                <Download
                  size={20}
                  className="shrink-0 transition-transform group-hover:translate-y-1"
                />
              </a>
            </Reveal>
          </div>
        </section>
      )}

      {/* ================ 3D Print services grid (imprimare 3D dept only) ================ */}
      {activeSlug === "tehnologii-imprimare-3d" && (
        <section
          className="border-b border-black"
          data-testid="print3d-services-section"
        >
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
            <Reveal>
              <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                <div>
                  <div className="font-mono-label text-black/60">Pagini dedicate</div>
                  <h2 className="mt-3 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9]">
                    Explorează<br />
                    <span className="italic font-normal">fiecare</span> tehnologie.
                  </h2>
                </div>
                <div className="font-mono-label text-black/50">[ 04 / 04 ]</div>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-black border border-black">
              {[
                {
                  n: "00",
                  t: "Soluții imprimare 3D",
                  d: "Hub-ul complet — scanare 3D, printare, inginerie inversă, matrițe, prototipuri.",
                  to: "/departamente/solutii-de-imprimare-3d",
                },
                {
                  n: "01",
                  t: "Plastic (SLS)",
                  d: "11 materiale — PA12, Flexa, PLA, PETG, TPU, PVA, PET-CF, PA6-CF, PC, ABS, ASA.",
                  to: "/imprimare-piese-3d-din-plastic",
                },
                {
                  n: "02",
                  t: "Fibră de carbon (CFR)",
                  d: "Markforged Mark Two — Onyx™, Nylon, Kevlar®, fibre continue, TPU 95A.",
                  to: "/imprimare-piese-3d-din-fibra-de-carbon",
                },
                {
                  n: "03",
                  t: "Metal (BMD)",
                  d: "Densități până la 98% — oțeluri, cupru, IN625, Ti64, sinterizare până la 1400°C.",
                  to: "/imprimare-piese-3d-din-metal",
                },
              ].map((s, i) => (
                <motion.div
                  key={s.to}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={s.to}
                    data-testid={`print3d-service-${i}`}
                    className="group bg-white hover:bg-black hover:text-white transition-all p-8 md:p-10 min-h-[260px] flex flex-col justify-between h-full"
                  >
                    <div className="flex items-start justify-between">
                      <div className="font-mono-label opacity-60">{s.n}</div>
                      <ArrowUpRight
                        size={22}
                        className="opacity-40 group-hover:opacity-100 group-hover:rotate-45 transition-all duration-500"
                      />
                    </div>
                    <div>
                      <div className="font-display font-medium uppercase text-2xl md:text-3xl tracking-tighter leading-[0.95]">
                        {s.t}
                      </div>
                      <div className="mt-3 text-sm opacity-70 leading-relaxed max-w-md">
                        {s.d}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================ 3D Print technologies section (imprimare 3D only) ================ */}
      {activeSlug === "tehnologii-imprimare-3d" && (
        <section
          className="relative border-b border-black bg-white overflow-hidden"
          data-testid="print3d-section"
        >
          {/* Gigantic background monogram */}
          <div
            aria-hidden
            className="absolute -right-4 -bottom-24 md:-right-16 md:-bottom-40 select-none pointer-events-none leading-none tracking-tighter text-[220px] md:text-[420px] font-display font-medium text-black/[0.04]"
          >
            +3D
          </div>

          <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
            {/* HEADER */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              <Reveal className="lg:col-span-8">
                <div className="font-mono-label text-black/60 flex items-center gap-3">
                  <span className="inline-block w-8 h-px bg-black" />
                  <span>Fabricație aditivă · Suntec</span>
                </div>
                <h2 className="mt-6 font-display font-medium uppercase text-5xl md:text-7xl tracking-tighter leading-[0.9]">
                  Printare 3D.<br />
                  <span className="italic font-normal">Soluții</span> pentru orice design.
                </h2>
                <p className="mt-8 max-w-2xl text-base md:text-lg text-black/80 leading-relaxed">
                  Transformăm planuri, schițe sau modele CAD în obiecte
                  fizice funcționale. Trei tehnologii, un singur atelier:
                  plastic (SLS), fibră de carbon (Markforged Mark&nbsp;Two) și
                  metal (BMD cu sinterizare).
                </p>
              </Reveal>

              <Reveal delay={0.15} className="lg:col-span-4 lg:justify-self-end">
                <div className="border-l-2 border-black pl-6 max-w-md">
                  <div className="font-mono-label text-black/60 mb-3">Livrare</div>
                  <div className="font-display font-medium uppercase text-2xl md:text-3xl tracking-tighter leading-[1.05]">
                    Prototipuri<br />
                    <span className="italic font-normal">& serii mici</span><br />
                    1–100 buc.
                  </div>
                </div>
              </Reveal>
            </div>

            {/* PILLARS — 4 differentiators */}
            <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-black border border-black">
              {[
                { n: "01", t: "Rapiditate", d: "Comenzi mici, termen scurt — de la brief la piesa livrată." },
                { n: "02", t: "Personalizare", d: "Piese și componente adaptate exact la specificațiile clientului." },
                { n: "03", t: "Calitate de top", d: "Precizie dimensională, densitate ridicată, finisaj de nivel industrial." },
                { n: "04", t: "Sustenabilitate", d: "Mai puțin material, mai puțină energie, cost per bucată optimizat." },
              ].map((p, i) => (
                <motion.div
                  key={p.n}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
                  className="bg-white p-6 md:p-8"
                >
                  <div className="font-mono-label text-black/40">{p.n}</div>
                  <div className="mt-4 font-display font-medium uppercase text-xl md:text-2xl tracking-tight leading-[1.05]">
                    {p.t}
                  </div>
                  <div className="mt-3 text-sm text-black/75 leading-relaxed">{p.d}</div>
                </motion.div>
              ))}
            </div>

            {/* THREE TECHNOLOGY CHAPTERS */}
            <div className="mt-24">
              <Reveal>
                <div className="font-mono-label text-black/60">Trei tehnologii, un atelier</div>
                <h3 className="mt-3 font-display font-medium uppercase text-3xl md:text-5xl tracking-tighter leading-[0.95]">
                  Plastic · Fibră de carbon · Metal.
                </h3>
              </Reveal>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-black border border-black">
                {[
                  {
                    tag: "Plastic · SLS",
                    n: "01",
                    title: "Selective Laser Sintering",
                    pitch: "Rapid · versatil · flexibil · accesibil.",
                    materials: "PA12 Smooth · Flexa Grey · PLA · PETG · TPU · PVA · PET-CF · PA6-CF · PC · ABS · ASA.",
                    apps: "Prototipuri funcționale, componente finale, protecții personalizate, piese care necesită duritate și flexibilitate.",
                    dark: false,
                  },
                  {
                    tag: "Fibră de carbon · CFR",
                    n: "02",
                    title: "Markforged Mark Two",
                    pitch: "Rezistența aluminiului · geometrii complexe.",
                    materials: "Fibră de carbon continuă · fibră de sticlă · Kevlar® · fibră HSHT · Onyx™ · Nylon · TPU 95A · PLA.",
                    apps: "Piese auto, brațe robotice, matrițe și puncții, unelte de formare, componente rezistente la coroziune și uzură.",
                    dark: true,
                  },
                  {
                    tag: "Metal · BMD",
                    n: "03",
                    title: "Bound Metal Deposition",
                    pitch: "Performanță · durabilitate · flexibilitate.",
                    materials: "Oțel 17-4 PH · 316L · 4140 · A2 · D2 · H13 · Cupru · Nichel IN625 · Titan Ti64.",
                    apps: "Componente aerospațiale, implanturi medicale, aplicații maritime, prelucrare metale, industria petrolieră.",
                    dark: false,
                  },
                ].map((tech, i) => (
                  <motion.div
                    key={tech.n}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.7, delay: i * 0.08 }}
                    className={
                      (tech.dark ? "bg-black text-white " : "bg-white text-black ") +
                      "p-8 md:p-10 min-h-[420px] flex flex-col"
                    }
                    data-testid={`print3d-tech-${i}`}
                  >
                    <div className={"font-mono-label " + (tech.dark ? "text-white/50" : "text-black/50")}>
                      {tech.n} / {tech.tag}
                    </div>
                    <div className="mt-6 font-display font-medium uppercase text-2xl md:text-3xl tracking-tighter leading-[1.05]">
                      {tech.title}
                    </div>
                    <div className={"mt-3 italic text-base md:text-lg " + (tech.dark ? "text-white/85" : "text-black/85")}>
                      {tech.pitch}
                    </div>

                    <div className="mt-8 border-t border-current/20 pt-6">
                      <div className={"font-mono-label mb-2 " + (tech.dark ? "text-white/50" : "text-black/50")}>
                        Materiale
                      </div>
                      <div className={"text-sm leading-relaxed " + (tech.dark ? "text-white/85" : "text-black/85")}>
                        {tech.materials}
                      </div>
                    </div>

                    <div className="mt-6 border-t border-current/20 pt-6 mt-auto">
                      <div className={"font-mono-label mb-2 " + (tech.dark ? "text-white/50" : "text-black/50")}>
                        Aplicații
                      </div>
                      <div className={"text-sm leading-relaxed " + (tech.dark ? "text-white/85" : "text-black/85")}>
                        {tech.apps}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* PROCESS & INDUSTRIES */}
            <div className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
              <Reveal className="lg:col-span-6">
                <div className="font-mono-label text-black/60">Cum lucrăm</div>
                <h3 className="mt-3 font-display font-medium uppercase text-3xl md:text-4xl tracking-tighter">
                  Trei pași, o piesă gata.
                </h3>
                <div className="mt-8 space-y-6 max-w-xl">
                  {[
                    { n: "01", t: "Pregătire", d: "Model CAD sau scanare 3D, alegerea materialului și a tehnologiei potrivite pentru cerințe." },
                    { n: "02", t: "Imprimare", d: "SLS / CFR / BMD — cu setări optimizate pentru viteză sau pentru caracteristici fine." },
                    { n: "03", t: "Sinterizare / finisare", d: "Cuptor pentru piese metalice (densitate 98%), tratamente și verificări dimensionale." },
                  ].map((s) => (
                    <div key={s.n} className="grid grid-cols-[auto_1fr] gap-6 items-start border-t border-black pt-6">
                      <div className="font-mono-label text-black/40">{s.n}</div>
                      <div>
                        <div className="font-display font-medium uppercase text-xl tracking-tight">{s.t}</div>
                        <div className="mt-2 text-base text-black/75 leading-relaxed">{s.d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.15} className="lg:col-span-6">
                <div className="font-mono-label text-black/60">Industrii deservite</div>
                <h3 className="mt-3 font-display font-medium uppercase text-3xl md:text-4xl tracking-tighter">
                  De la <span className="italic font-normal">prototip</span><br />
                  la producție finală.
                </h3>
                <div className="mt-8 flex flex-wrap gap-2 max-w-xl">
                  {[
                    "Automotive", "Aerospațial", "Feroviar", "Medical",
                    "Construcții", "Militar", "Educație & cercetare",
                    "Arhitectură & artă", "Produse de larg consum",
                  ].map((u) => (
                    <span
                      key={u}
                      className="inline-block border border-black px-4 py-2 text-sm hover:bg-black hover:text-white transition-colors"
                    >
                      {u}
                    </span>
                  ))}
                </div>
                <p className="mt-8 text-base text-black/70 max-w-md leading-relaxed">
                  Piese cu densitate până la 98% și rezistență
                  comparabilă cu aluminiul prelucrat mecanic — la
                  10× costul unei matrițe tradiționale.
                </p>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* ================ Barello brand section (dotari-horeca only) ================ */}
      {activeSlug === "dotari-horeca" && (
        <section
          className="relative border-b border-black bg-white overflow-hidden"
          data-testid="barello-brand-section"
        >
          {/* Barello wordmark as gigantic background monogram */}
          <div
            aria-hidden
            className="absolute -left-6 -bottom-24 md:-left-16 md:-bottom-40 select-none pointer-events-none leading-none tracking-tighter text-[220px] md:text-[440px] font-display font-medium lowercase"
            style={{ color: "rgba(214, 24, 11, 0.045)" }}
          >
            barello
          </div>

          <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
            {/* HEADER — brand card */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              <Reveal className="lg:col-span-7">
                <div className="font-mono-label text-black/60 flex items-center gap-3">
                  <span className="inline-block w-8 h-px bg-black" />
                  <span>Brand sub umbrela SUNTEC</span>
                </div>

                {/* Barello logo (image asset) */}
                <img
                  src="/logo-barello.png"
                  alt="Barello Professional HoReCa Systems"
                  className="mt-6 h-24 md:h-40 w-auto"
                  data-testid="barello-wordmark"
                />

                <h2 className="mt-10 font-display font-medium uppercase text-3xl md:text-5xl tracking-tighter leading-[0.95] max-w-3xl">
                  Soluții complete.<br />
                  <span className="italic font-normal">Performanță</span> pe termen lung.
                </h2>
              </Reveal>

              <Reveal delay={0.15} className="lg:col-span-5 lg:justify-self-end">
                <div className="border-l-2 border-black pl-6 max-w-md">
                  <div className="font-mono-label text-black/60 mb-3">Rol în grup</div>
                  <p className="text-base md:text-lg leading-relaxed text-black/85">
                    Barello este marca SUNTEC dedicată industriei HoReCa —
                    un partener unic care duce proiectul de la prima
                    discuție la mentenanța pe termen lung.
                  </p>
                </div>
              </Reveal>
            </div>

            {/* PILLARS — 4 mini blocks */}
            <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-black border border-black">
              {[
                { n: "01", t: "Un singur partener", d: "De la consultanță și proiectare, până la implementare la cheie și service." },
                { n: "02", t: "Echipamente premium", d: "Branduri europene de top, testate pe teren pentru performanță și fiabilitate." },
                { n: "03", t: "Proces integrat", d: "Claritate și control în fiecare etapă — brief, plan, livrare, punere în funcțiune." },
                { n: "04", t: "100+ proiecte", d: "Referințe în restaurante, hoteluri, cafenele, pizzerii, cofetării și geleterii." },
              ].map((p, i) => (
                <motion.div
                  key={p.n}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
                  className="bg-white p-6 md:p-8"
                >
                  <div className="font-mono-label text-black/40">{p.n}</div>
                  <div className="mt-4 font-display font-medium uppercase text-xl md:text-2xl tracking-tight leading-[1.05]">
                    {p.t}
                  </div>
                  <div className="mt-3 text-sm text-black/75 leading-relaxed">{p.d}</div>
                </motion.div>
              ))}
            </div>

            {/* CATEGORIES + UNITS */}
            <div className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
              <Reveal className="lg:col-span-6">
                <div className="font-mono-label text-black/60">Categorii echipamente</div>
                <h3 className="mt-3 font-display font-medium uppercase text-3xl md:text-4xl tracking-tighter">
                  14 categorii, un ecosistem.
                </h3>
                <div className="mt-8 grid grid-cols-2 gap-y-3 gap-x-6 max-w-xl">
                  {[
                    "Echipamente de gătire",
                    "Refrigerare profesională",
                    "Spălare profesională",
                    "Mobilier inox profesional",
                    "Preparare",
                    "Pizza",
                    "Brutărie & cofetărie",
                    "Gelaterie",
                    "Bar",
                    "Fast food",
                    "Self service",
                    "Spălătorie profesională",
                    "Ustensile & consumabile",
                    "Design & proiectare",
                  ].map((c, i) => (
                    <div key={c} className="flex items-baseline gap-3 text-base text-black/85">
                      <span className="font-mono-label text-black/40 text-[10px]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>{c}</span>
                    </div>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.15} className="lg:col-span-6">
                <div className="font-mono-label text-black/60">Tipuri de unități HoReCa</div>
                <h3 className="mt-3 font-display font-medium uppercase text-3xl md:text-4xl tracking-tighter">
                  O gamă completă,<br />
                  <span className="italic font-normal">pentru fiecare</span> nevoie.
                </h3>
                <div className="mt-8 flex flex-wrap gap-2 max-w-xl">
                  {[
                    "Restaurant", "Hotel", "Cafenea", "Pizzerie",
                    "Bar", "Cofetărie / Patiserie", "Fast food",
                    "Gelaterie", "Self service", "Spălătorie profesională",
                  ].map((u) => (
                    <span
                      key={u}
                      className="inline-block border border-black px-4 py-2 text-sm hover:bg-black hover:text-white transition-colors"
                    >
                      {u}
                    </span>
                  ))}
                </div>
                <p className="mt-8 text-base text-black/70 max-w-md leading-relaxed">
                  Tot ce are nevoie bucătăria ta, dintr-un singur loc.
                </p>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* ================ Brochure download (HoReCa only) ================ */}
      {activeSlug === "dotari-horeca" && (
        <section
          className="relative border-b border-black bg-black text-white overflow-hidden"
          data-testid="brochure-download-section"
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, transparent 0 22px, #fff 22px 23px)",
            }}
          />
          <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <Reveal className="lg:col-span-7">
              <div className="font-mono-label text-white/50 flex items-center gap-3">
                <span className="inline-block w-8 h-px bg-white/50" />
                <FileText size={12} /> Broșură · PDF
              </div>
              <h2 className="mt-5 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.92]">
                Descarcă broșura<br />
                <span className="italic font-normal">Barello HoReCa</span>
              </h2>
              <p className="mt-6 max-w-xl text-base text-white/70 leading-relaxed">
                Catalog complet Barello — echipamente profesionale pentru
                bucătării, cofetării, geleterii, baruri și unități HoReCa,
                testate în peste 100 de proiecte reale.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5 lg:justify-self-end w-full lg:w-auto">
              <a
                href="/brosura-barello.pdf"
                download
                data-testid="brochure-download-btn"
                className="group font-mono-label bg-white text-black px-8 py-6 hover:bg-black hover:text-white border border-white inline-flex items-center justify-between gap-6 transition-all w-full lg:w-auto"
              >
                <span className="flex flex-col items-start gap-1">
                  <span className="text-[10px] opacity-60">Format PDF · ~1 MB</span>
                  <span>Descarcă broșura</span>
                </span>
                <Download
                  size={20}
                  className="shrink-0 transition-transform group-hover:translate-y-1"
                />
              </a>
            </Reveal>
          </div>
        </section>
      )}

      {/* ================ CTA ================ */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-b border-black">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <div className="font-mono-label text-black/60">{t("section.dept.cta.eyebrow")}</div>
            {activeSlug === "achizitii-proiecte-europene" ? (
              <h2 className="mt-3 font-display font-medium uppercase text-5xl md:text-7xl tracking-tighter leading-[0.9]">
                Ai câștigat un proiect european<span className="italic font-normal">?</span>
                <br />
                Achiziționează-ți echipamentele cu <span className="italic font-normal">SUNTEC.</span>
              </h2>
            ) : (
              <h2 className="mt-3 font-display font-medium uppercase text-5xl md:text-7xl tracking-tighter leading-[0.9]">
                Solicită o<br />
                <span className="italic font-normal">ofertă</span> pentru {dept.name}.
              </h2>
            )}
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-4 flex lg:justify-end">
            <SmartLink
              to={t("btn.dept.cta.link", url.contact)}
              data-testid="dept-cta-contact"
              className="group font-mono-label bg-black text-white px-8 py-5 hover:bg-white hover:text-black border border-black inline-flex items-center gap-3 transition-all"
            >
              {activeSlug === "achizitii-proiecte-europene" ? "Contactează-ne acum" : t("btn.dept.cta.label")}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </SmartLink>
          </Reveal>
        </div>
      </section>

      {/* ================ Prev / Next nav ================ */}
      {prev && next && (
        <section className="grid grid-cols-2 border-b border-black">
          <Link
            to={url.dept(prev.slug)}
            data-testid="prev-dept"
            className="group p-8 md:p-14 border-r border-black hover:bg-black hover:text-white transition-all"
          >
            <div className="font-mono-label opacity-60 flex items-center gap-2">
              <ArrowLeft size={12} className="transition-transform group-hover:-translate-x-1" />
              Anterior · {prev.number}
            </div>
            <div className="mt-4 font-display font-medium uppercase text-2xl md:text-5xl tracking-tighter leading-[0.9]">
              {prev.name}
            </div>
          </Link>
          <Link
            to={url.dept(next.slug)}
            data-testid="next-dept"
            className="group p-8 md:p-14 hover:bg-black hover:text-white transition-all text-right"
          >
            <div className="font-mono-label opacity-60 flex items-center justify-end gap-2">
              Următor · {next.number}
              <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
            </div>
            <div className="mt-4 font-display font-medium uppercase text-2xl md:text-5xl tracking-tighter leading-[0.9]">
              {next.name}
            </div>
          </Link>
        </section>
      )}

      {/* Lightbox */}
      <Lightbox
        images={gallery}
        index={lbIndex}
        onClose={closeLb}
        onPrev={prevLb}
        onNext={nextLb}
      />
    </div>
  );
}
